import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button } from '@naf-simba/elements-buttons';
import { breakpoints } from '@naf/theme';
import { ArticleHeader } from '@naf-simba/sections-headers';
import type { SanityBlock } from '@sanity/client';
import { AdvancedImage, responsive } from '@cloudinary/react';
import { useCloudinary } from '@hooks/Cloudinary/useCloudinary';
import { useDocumentUrlWithNode } from '@hooks/Documents/useDocumentUrl';
import { useWindowResize } from '@hooks/Window/useWindowResize';
import { mapSimbaVariant } from '@utils/simba';
import { InternalLinkType } from '@appTypes/internalLinkType';
import { CloudinaryImageType } from '@appTypes/CloudinaryImageType';

export interface HeroWithTitleAndWideImageType {
  heroVariant: 'heroWithTitleAndWideImage' | 'heroWithTitleAndWideAndShortImage';
  header: string;
  image: CloudinaryImageType;
  mobileImage?: CloudinaryImageType;
  body?: SanityBlock[];
  ingress?: string;
  ctaButton?: {
    buttonText: string;
    data: InternalLinkType;
  };
  ctaInternalLink?: {
    text?: string;
    referenceComponent?: InternalLinkType;
  };
  ctaExternalLink?: {
    text?: string;
    link?: string;
    type: string;
    blank?: boolean;
  };
  overlay?: boolean;
  halfScreen?: boolean;
}

export const HeroWithTitleAndWideImage = ({
  hero: { header, ctaInternalLink, image, mobileImage, ingress, overlay },
}: {
  hero: HeroWithTitleAndWideImageType;
}) => {
  const { getResponsiveImage } = useCloudinary();
  const navigate = useNavigate();
  const ctaUrl = useDocumentUrlWithNode(ctaInternalLink?.referenceComponent);
  const ctaButtonVariant = mapSimbaVariant('secondary');
  const linkButton =
    ctaInternalLink && ctaUrl ? (
      <Button
        variant={ctaButtonVariant.variant}
        color={ctaButtonVariant.color}
        shade={ctaButtonVariant.shade}
        onClick={() => {
          if (ctaUrl) {
            navigate(ctaUrl);
          }
        }}
      >
        {ctaInternalLink.text}
      </Button>
    ) : undefined;

  const [mainImage, setMainImage] = useState(
    getResponsiveImage(image?.publicId, {
      width: 1120,
      focusMethod: image?.focus,
    }),
  );

  const setResponsiveImage = useCallback(() => {
    const width = Math.round(window.innerWidth);
    const height = Math.round(window.innerHeight);
    const heroImage = width <= parseInt(breakpoints.s, 10) && mobileImage?.publicId ? mobileImage : image;
    if (typeof window !== 'undefined') {
      // Use vheight for small screens, and vw for large screens
      if (width <= parseInt(breakpoints.m, 10)) {
        setMainImage(
          getResponsiveImage(heroImage?.publicId, {
            width,
            height,
            focusMethod: heroImage?.focus,
          }),
        );
      } else {
        setMainImage(
          getResponsiveImage(heroImage?.publicId, {
            width,
            focusMethod: heroImage?.focus,
          }),
        );
      }
    }
  }, [mobileImage, image, getResponsiveImage]);

  useEffect(() => {
    setResponsiveImage();
  }, [setResponsiveImage]);

  useWindowResize(() => {
    setResponsiveImage();
  });

  if (mainImage) {
    return (
      <ArticleHeader
        imageOrVideo={
          <AdvancedImage
            plugins={[responsive({ steps: 200 })]}
            cldImg={mainImage}
            alt={image.alt || image.altText || image.caption}
            image={mainImage}
          />
        }
        ingress={ingress}
        title={header}
        ctaButton={linkButton}
        headingVariant="display"
        height="full-viewport"
        wideGrid
        nafBranded={false}
        overlay={overlay}
      />
    );
  }
  return (
    <ArticleHeader
      ingress={ingress}
      title={header}
      headingVariant="display"
      height="full-viewport"
      wideGrid
      nafBranded={false}
    />
  );
};
